/**
 * Authoritative state refresh screen (Task 12).
 * One form, all cards: previous observed values, live deltas, unreconciled_delta, staleness.
 * Submits drafts only — persistence (append observation + evidence) is the caller's job.
 */

import { useState, type FormEvent } from 'react';
import { formatCentsDisplay } from '../../domain/recommend/explain';
import type { Cents } from '../../domain/money';
import { asCents } from '../../domain/money';
import type {
  RefreshMoneyField,
  StateRefreshCardDraft,
  StateRefreshCardView,
  StateRefreshSubmitPayload,
} from './types';

export type StateRefreshScreenProps = {
  cards: readonly StateRefreshCardView[];
  onSubmit: (payload: StateRefreshSubmitPayload) => Promise<void>;
};

type MoneyKey =
  'current_balance' | 'available_credit' | 'credit_limit' | 'statement_balance' | 'minimum_payment';

const MONEY_FIELDS: readonly {
  key: MoneyKey;
  label: string;
  draftKey: keyof StateRefreshCardDraft;
}[] = [
  { key: 'current_balance', label: 'Current balance', draftKey: 'current_balance_raw' },
  { key: 'available_credit', label: 'Available credit', draftKey: 'available_credit_raw' },
  { key: 'credit_limit', label: 'Credit limit', draftKey: 'credit_limit_raw' },
  { key: 'statement_balance', label: 'Statement balance', draftKey: 'statement_balance_raw' },
  { key: 'minimum_payment', label: 'Minimum payment', draftKey: 'minimum_payment_raw' },
];

function emptyDraft(cardId: string): StateRefreshCardDraft {
  return {
    card_id: cardId,
    current_balance_raw: '',
    available_credit_raw: '',
    credit_limit_raw: '',
    statement_balance_raw: '',
    minimum_payment_raw: '',
    payment_due_date_raw: '',
  };
}

function formatMoney(cents: Cents | null): string {
  if (cents === null) return '—';
  return formatCentsDisplay(cents);
}

function parseAmountToCents(raw: string): Cents | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (match === null) return null;
  const dollars = BigInt(match[1] ?? '0');
  const frac = match[2] ?? '';
  const centsPart = frac.length === 0 ? 0n : frac.length === 1 ? BigInt(frac) * 10n : BigInt(frac);
  return asCents(dollars * 100n + centsPart);
}

function moneyDelta(previous: Cents | null, raw: string): Cents | null {
  const next = parseAmountToCents(raw);
  if (next === null || previous === null) return null;
  return asCents(next - previous);
}

function formatDelta(delta: Cents | null): string {
  if (delta === null) return '—';
  const asBig = BigInt(delta);
  if (asBig === 0n) return '$0.00';
  if (asBig < 0n) {
    return `−${formatCentsDisplay(-asBig)}`;
  }
  return `+${formatCentsDisplay(asBig)}`;
}

function observedAtLabel(at: string | null): string {
  if (at === null) return 'never observed';
  return `observed · ${at.slice(0, 10)}`;
}

function moneyFieldOf(card: StateRefreshCardView, key: MoneyKey): RefreshMoneyField {
  return card[key];
}

function CardRefreshSection(props: {
  card: StateRefreshCardView;
  draft: StateRefreshCardDraft;
  onChange: (next: StateRefreshCardDraft) => void;
}): JSX.Element {
  const { card, draft, onChange } = props;

  return (
    <fieldset
      data-testid="state-refresh-card"
      data-card-id={card.card_id}
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-4"
    >
      <legend className="px-1 text-base font-medium text-slate-100">{card.label}</legend>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        {card.is_stale ? (
          <span
            data-testid="staleness-flag"
            className="rounded border border-amber-800 px-2 py-0.5 font-medium text-amber-200"
          >
            Stale · {card.state_age_days ?? '—'}d (TTL {card.financial_staleness_days}d)
          </span>
        ) : (
          <span className="rounded border border-slate-700 px-2 py-0.5 text-slate-400">
            Age · {card.state_age_days === null ? '—' : `${card.state_age_days}d`}
          </span>
        )}
        <span
          data-testid="unreconciled-delta"
          className="rounded border border-slate-700 px-2 py-0.5 text-slate-300"
        >
          Unreconciled Δ {formatMoney(card.unreconciled_delta_cents)}
        </span>
        <span className="text-slate-500">
          Est. posted {formatMoney(card.estimated_posted_balance_cents)}
        </span>
      </div>

      <div className="mt-3 flex flex-col gap-3">
        {MONEY_FIELDS.map(({ key, label, draftKey }) => {
          const field = moneyFieldOf(card, key);
          const raw = String(draft[draftKey] ?? '');
          const delta = moneyDelta(field.previous_cents, raw);
          const inputId = `${card.card_id}-${key}`;
          return (
            <div key={key} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
                <span className="text-sm text-slate-200">{formatMoney(field.previous_cents)}</span>
                <span className="text-xs text-slate-500">{observedAtLabel(field.observed_at)}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <label htmlFor={inputId} className="text-xs text-slate-500">
                  {label} (new)
                </label>
                <input
                  id={inputId}
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="leave blank to skip"
                  value={raw}
                  onChange={(e) => onChange({ ...draft, [draftKey]: e.target.value })}
                  className="rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100"
                />
              </div>
              <div
                data-testid={`delta-${key}`}
                className="min-w-[4.5rem] pb-1.5 text-right text-sm text-slate-300"
              >
                {formatDelta(delta)}
              </div>
            </div>
          );
        })}

        <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs uppercase tracking-wide text-slate-500">Due date</span>
            <span className="text-sm text-slate-200">{card.payment_due_date.previous ?? '—'}</span>
            <span className="text-xs text-slate-500">
              {observedAtLabel(card.payment_due_date.observed_at)}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <label htmlFor={`${card.card_id}-payment_due_date`} className="text-xs text-slate-500">
              Due date (new)
            </label>
            <input
              id={`${card.card_id}-payment_due_date`}
              type="date"
              value={draft.payment_due_date_raw}
              onChange={(e) => onChange({ ...draft, payment_due_date_raw: e.target.value })}
              className="rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100"
            />
          </div>
          <div className="min-w-[4.5rem] pb-1.5 text-right text-sm text-slate-500">—</div>
        </div>
      </div>
    </fieldset>
  );
}

export function StateRefreshScreen({ cards, onSubmit }: StateRefreshScreenProps): JSX.Element {
  const [drafts, setDrafts] = useState<Record<string, StateRefreshCardDraft>>(() => {
    const init: Record<string, StateRefreshCardDraft> = {};
    for (const card of cards) {
      init[card.card_id] = emptyDraft(card.card_id);
    }
    return init;
  });
  const [saving, setSaving] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      const payload: StateRefreshSubmitPayload = {
        cards: cards.map((card) => drafts[card.card_id] ?? emptyDraft(card.card_id)),
      };
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section data-testid="state-refresh-screen" className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">State refresh</h2>
        <p className="mt-1 text-sm text-slate-400">
          One form for every card. Previous observed values stay visible; deltas update as you type.
          Unreconciled Δ is the ledger signal since the last observation — do not absorb it
          silently.
        </p>
      </div>

      {cards.length === 0 ? (
        <p className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
          No cards in the portfolio yet.
        </p>
      ) : (
        <form
          data-testid="state-refresh-form"
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="flex flex-col gap-4"
        >
          {cards.map((card) => (
            <CardRefreshSection
              key={card.card_id}
              card={card}
              draft={drafts[card.card_id] ?? emptyDraft(card.card_id)}
              onChange={(next) => setDrafts((prev) => ({ ...prev, [card.card_id]: next }))}
            />
          ))}
          <button
            type="submit"
            disabled={saving}
            className="rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save observations'}
          </button>
        </form>
      )}
    </section>
  );
}
